import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import type { PublicSurveyQuestion, SubmitAnswerRequest } from "@/types/api"
import { extractErrorDetailField } from "@/lib/error-messages"

// Questionnaire public (/survey/:token) : mêmes règles que le serveur
// (`validate_survey_answers`, surveys/public.rs). Le contrôle évite un aller-retour ;
// le serveur reste juge.

/** Longueurs maximales d'une réponse texte (SURVEY_TEXT_*_MAX_CHARS côté serveur). */
export const SURVEY_TEXT_SHORT_MAX_CHARS = 500
export const SURVEY_TEXT_LONG_MAX_CHARS = 5000

/** Réponse saisie à une question, selon son type. */
export type SurveyAnswerValue =
  | { kind: "integer"; value: number }
  | { kind: "text"; value: string }
  | { kind: "boolean"; value: boolean }
  | { kind: "choices"; values: string[] }

export type SurveyAnswers = Readonly<Record<string, SurveyAnswerValue>>

/** Texte « Précisez » du choix « Autre », par question. */
export type SurveyOtherTexts = Readonly<Record<string, string>>

/** Longueur maximale du texte « Précisez ». Même borne qu'un texte court. */
export const SURVEY_OTHER_TEXT_MAX_CHARS = SURVEY_TEXT_SHORT_MAX_CHARS

/** Point d'une échelle : la valeur envoyée et son libellé. */
export interface ScalePoint {
  value: number
  label: string
}

const LIKERT_LABELS = ["Pas du tout d'accord", "Plutôt pas d'accord", "Ni d'accord ni pas d'accord", "Plutôt d'accord", "Tout à fait d'accord"]

function range(min: number, max: number): number[] {
  return Array.from({ length: max - min + 1 }, (_, index) => min + index)
}

/**
 * Points d'une échelle, aux bornes que lit la satisfaction (vue 618) :
 * 1 à 5, 1 à 10, NPS de 0 à 10, Likert de 1 à 5 libellé. `none` hors échelle.
 */
export function scalePoints(typeCode: string): O.Option<ScalePoint[]> {
  switch (typeCode) {
    case "rating_5":
      return O.some(range(1, 5).map((value) => ({ value, label: String(value) })))
    case "rating_10":
      return O.some(range(1, 10).map((value) => ({ value, label: String(value) })))
    case "nps":
      return O.some(range(0, 10).map((value) => ({ value, label: String(value) })))
    case "likert_5":
      return O.some(LIKERT_LABELS.map((label, index) => ({ value: index + 1, label })))
    default:
      return O.none
  }
}

/** Le questionnaire public n'a aucun dépôt de fichier : une telle question n'est ni répondue ni exigée. */
export function isAnswerable(question: Pick<PublicSurveyQuestion, "question_type_code">): boolean {
  return question.question_type_code !== "file_upload"
}

function isFilled(answer: SurveyAnswerValue): boolean {
  switch (answer.kind) {
    case "integer":
    case "boolean":
      return true
    case "text":
      return answer.value.trim().length > 0
    case "choices":
      return answer.values.length > 0
  }
}

function answerOf(answers: SurveyAnswers, questionId: string): O.Option<SurveyAnswerValue> {
  return pipe(O.fromNullable(answers[questionId]), O.filter(isFilled))
}

/**
 * Bornes d'une réponse texte, comme `accept_answer` côté serveur.
 * La question fixe min_length et max_length.
 * Le type de la question plafonne max_length.
 * @example textLengthBounds({ question_type_code: "text_short", min_length: 3, max_length: 20 }) // { min: 3, max: 20 }
 */
export function textLengthBounds(
  question: Pick<PublicSurveyQuestion, "question_type_code" | "min_length" | "max_length">,
): { min: number; max: number } {
  const typeMax: number = question.question_type_code === "text_short" ? SURVEY_TEXT_SHORT_MAX_CHARS : SURVEY_TEXT_LONG_MAX_CHARS
  const max = pipe(
    O.fromNullable(question.max_length),
    O.filter((length) => length > 0),
    O.map((length) => Math.min(length, typeMax)),
    O.getOrElse(() => typeMax),
  )
  const min = pipe(
    O.fromNullable(question.min_length),
    O.filter((length) => length > 0),
    O.getOrElse(() => 0),
  )
  return { min, max }
}

/**
 * Le choix retenu à la question est son option « Autre ».
 * @example otherOptionChosen(question, O.some({ kind: "text", value: "Autre" })) // true
 */
export function otherOptionChosen(question: Pick<PublicSurveyQuestion, "options">, answer: O.Option<SurveyAnswerValue>): boolean {
  const otherValues = new Set(question.options.filter((option) => option.is_other_option).map((option) => option.value))
  return pipe(
    answer,
    O.exists((value) => {
      if (value.kind === "text") return otherValues.has(value.value)
      if (value.kind === "choices") return value.values.some((chosen) => otherValues.has(chosen))
      return false
    }),
  )
}

/** Questions obligatoires encore sans réponse. */
export function missingRequiredQuestions(
  questions: ReadonlyArray<PublicSurveyQuestion>,
  answers: SurveyAnswers,
): PublicSurveyQuestion[] {
  return questions.filter((question) => question.is_required && isAnswerable(question) && O.isNone(answerOf(answers, question.id)))
}

/** Texte « Précisez » à envoyer. Il faut l'option « Autre » et un texte. */
function otherTextOf(question: PublicSurveyQuestion, answer: SurveyAnswerValue, others: SurveyOtherTexts): Partial<SubmitAnswerRequest> {
  return pipe(
    O.fromNullable(others[question.id]),
    O.map((text) => text.trim()),
    O.filter((text) => text.length > 0 && otherOptionChosen(question, O.some(answer))),
    O.match(
      () => ({}),
      (text) => ({ other_text: text }),
    ),
  )
}

/** Une réponse par question répondue, dans le champ de son type ; une réponse vide n'est pas envoyée. */
export function buildSurveyAnswers(
  questions: ReadonlyArray<PublicSurveyQuestion>,
  answers: SurveyAnswers,
  others: SurveyOtherTexts = {},
): SubmitAnswerRequest[] {
  return questions.filter(isAnswerable).flatMap((question) =>
    pipe(
      answerOf(answers, question.id),
      O.match(
        (): SubmitAnswerRequest[] => [],
        (answer): SubmitAnswerRequest[] => {
          switch (answer.kind) {
            case "integer":
              return [{ question_id: question.id, value_integer: answer.value }]
            case "boolean":
              return [{ question_id: question.id, value_boolean: answer.value }]
            case "text":
              return [{ question_id: question.id, value_text: answer.value.trim(), ...otherTextOf(question, answer, others) }]
            case "choices":
              return [{ question_id: question.id, value_json: answer.values, ...otherTextOf(question, answer, others) }]
          }
        },
      ),
    ),
  )
}

/** Motifs de refus du serveur, en clair. */
const FIELD_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  missing_required: "Réponse obligatoire.",
  missing_value: "Réponse obligatoire.",
  out_of_range: "Valeur hors de l'échelle proposée.",
  too_long: "Réponse trop longue.",
  too_short: "Réponse trop courte.",
  unknown_option: "Ce choix ne fait pas partie de la question.",
  invalid_date: "Date invalide.",
  duplicate: "Réponse envoyée deux fois.",
  unsupported_type: "Cette question ne se remplit pas en ligne.",
}

/**
 * Message d'un motif. Un motif inconnu donne un message générique.
 * @example surveyFieldErrorMessage("too_long") // "Réponse trop longue."
 */
export function surveyFieldErrorMessage(reason: string): string {
  return pipe(
    O.fromNullable(FIELD_ERROR_MESSAGES[reason]),
    O.getOrElse(() => "Réponse refusée."),
  )
}

function stringField(value: unknown, key: string): O.Option<string> {
  return pipe(
    O.fromNullable(value),
    O.filter((candidate): candidate is Record<string, unknown> => typeof candidate === "object"),
    O.chain((fields) => O.fromNullable(fields[key])),
    O.filter((field): field is string => typeof field === "string"),
  )
}

/**
 * Motif de refus par question, lu dans un 422 SURVEY_ANSWERS_INVALID.
 * Sources : `details.invalid` et `details.missing_required`.
 * Toute autre erreur ne désigne aucune question.
 * @example surveyFieldErrors(error) // { "q-city": "too_short" }
 */
export function surveyFieldErrors(error: unknown): Readonly<Record<string, string>> {
  const invalid = pipe(
    extractErrorDetailField(error, "invalid"),
    O.filter(Array.isArray),
    O.map((items: unknown[]) =>
      items.flatMap((item) =>
        pipe(
          O.Do,
          O.apS("id", stringField(item, "question_id")),
          O.apS("reason", stringField(item, "reason")),
          O.match(
            (): [string, string][] => [],
            ({ id, reason }): [string, string][] => [[id, reason]],
          ),
        ),
      ),
    ),
    O.getOrElse((): [string, string][] => []),
  )
  const missing = pipe(
    extractErrorDetailField(error, "missing_required"),
    O.filter(Array.isArray),
    O.map((ids: unknown[]) =>
      ids.filter((id): id is string => typeof id === "string").map((id): [string, string] => [id, "missing_required"]),
    ),
    O.getOrElse((): [string, string][] => []),
  )
  return Object.fromEntries([...missing, ...invalid])
}
